import React, { useEffect, useMemo, useState } from "react";
import BadgeMultiSelect from "../BadgeMultiSelect";
import {
  useGetAllSponsorUserList,
  useSponsorNotificationUsers,
  useSetSponsorNotificationUsers,
} from "../../custom-hooks/queries";
import {
  isActiveSponsorUser,
  savedToOption,
  toPayload,
  userToOption,
} from "./orgNotificationsUtils";

/**
 * VQQ global notifications are on hold until sponsor-facing VQQ emails are wired
 * the same way as VQA. Flip to `true` to show the VQQ section again (BE endpoints
 * and HyperComply merge already exist).
 */
const SHOW_VQQ_GLOBAL_NOTIFICATIONS = false;

/**
 * Notifications tab on the Manage Sponsor Account screen.
 * Configures the sponsor's global notification recipients for VQA events
 * (and VQQ when SHOW_VQQ_GLOBAL_NOTIFICATIONS is enabled).
 * These users receive the same sponsor-facing notifications as the requester.
 */
export default function OrgNotificationsTab({ sponsorId }) {
  const { data: sponsorUsersData, isLoading: isLoadingUsers } =
    useGetAllSponsorUserList({
      currentPage: 1,
      id: sponsorId,
      search_text: "",
      page_size: 1000,
    });

  const {
    data: savedData,
    isLoading: isLoadingSaved,
    isFetching: isFetchingSaved,
  } = useSponsorNotificationUsers(sponsorId);

  const { mutateAsync: saveNotificationUsers, isPending: isSaving } =
    useSetSponsorNotificationUsers(sponsorId);

  const [isEditMode, setIsEditMode] = useState(false);
  const [vqqSelected, setVqqSelected] = useState([]);
  const [vqaSelected, setVqaSelected] = useState([]);
  const [banner, setBanner] = useState(null);

  const options = useMemo(() => {
    const items = sponsorUsersData?.items ?? [];
    return items
      .filter((u) => u?.email && isActiveSponsorUser(u))
      .map(userToOption)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [sponsorUsersData]);

  useEffect(() => {
    if (!savedData) return;
    setVqqSelected((savedData.vqq ?? []).map(savedToOption));
    setVqaSelected((savedData.vqa ?? []).map(savedToOption));
  }, [savedData]);

  const handleCancel = () => {
    setVqqSelected((savedData?.vqq ?? []).map(savedToOption));
    setVqaSelected((savedData?.vqa ?? []).map(savedToOption));
    setIsEditMode(false);
    setBanner(null);
  };

  const handleSave = async () => {
    try {
      // When VQQ UI is hidden, persist the last server-loaded VQQ list unchanged
      // so a VQA-only save cannot wipe future VQQ configuration.
      const vqqPayload = SHOW_VQQ_GLOBAL_NOTIFICATIONS
        ? toPayload(vqqSelected)
        : toPayload((savedData?.vqq ?? []).map(savedToOption));

      await saveNotificationUsers({
        vqq: vqqPayload,
        vqa: toPayload(vqaSelected),
      });
      setIsEditMode(false);
      setBanner({ type: "success", message: "Notification settings saved." });
    } catch (error) {
      console.error("Failed to save notification settings", error);
      setBanner({
        type: "error",
        message: "Failed to save notification settings. Please try again.",
      });
    }
  };

  const isLoading = isLoadingUsers || isLoadingSaved;
  const saveLabel = isSaving ? "Saving…" : "Save";

  return (
    <section className="w-full bg-white shadow-sm rounded-lg">
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Notifications
            </h2>
            <p className="mt-1 text-sm text-gray-500 max-w-2xl">
              {SHOW_VQQ_GLOBAL_NOTIFICATIONS
                ? "Set the default recipients that receive notifications for all Vendor Qualification Questionnaires (VQQ) and Vendor Qualification Assessments (VQA). These users receive the same sponsor-facing notifications as the requester."
                : "Set the default recipients that receive Vendor Qualification Assessment (VQA) notifications for this organization. These users receive the same sponsor-facing notifications as the requester."}
            </p>
          </div>
          <div className="flex-shrink-0">
            {isEditMode ? (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
                >
                  {saveLabel}
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setBanner(null);
                  setIsEditMode(true);
                }}
                disabled={isLoading || isFetchingSaved}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50"
              >
                Edit
              </button>
            )}
          </div>
        </div>

        {banner && (
          <div
            className={`mt-4 rounded-md px-4 py-2 text-sm ${
              banner.type === "success"
                ? "bg-green-50 text-green-800 ring-1 ring-inset ring-green-200"
                : "bg-red-50 text-red-800 ring-1 ring-inset ring-red-200"
            }`}
          >
            {banner.message}
          </div>
        )}
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 pb-8">
        {isLoading ? (
          <div className="py-10 text-center text-sm text-gray-500">
            Loading…
          </div>
        ) : (
          <div
            className={
              SHOW_VQQ_GLOBAL_NOTIFICATIONS
                ? "grid grid-cols-1 gap-8 md:grid-cols-2"
                : "grid grid-cols-1 gap-8 max-w-xl"
            }
          >
            {SHOW_VQQ_GLOBAL_NOTIFICATIONS && (
              <NotificationSection
                title="Vendor Qualification Questionnaires (VQQ)"
                description="Recipients are also set as owners on questionnaires sent through HyperComply."
                options={options}
                selected={vqqSelected}
                onChange={setVqqSelected}
                disabled={!isEditMode}
              />
            )}
            <NotificationSection
              title="Vendor Qualification Assessments (VQA)"
              description="Recipients receive the same VQA event notifications as the requester for this organization."
              options={options}
              selected={vqaSelected}
              onChange={setVqaSelected}
              disabled={!isEditMode}
            />
          </div>
        )}
      </div>
    </section>
  );
}

function NotificationSection({
  title,
  description,
  options,
  selected,
  onChange,
  disabled,
}) {
  return (
    <div className="rounded-lg border border-gray-200 p-4">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="mt-1 mb-3 text-xs text-gray-500">{description}</p>
      {disabled ? (
        <ReadOnlyList selected={selected} />
      ) : (
        <BadgeMultiSelect
          options={options}
          selected={selected}
          onChange={onChange}
          placeholder="Select users…"
        />
      )}
    </div>
  );
}

function ReadOnlyList({ selected }) {
  if (!selected?.length) {
    return (
      <p className="text-sm text-gray-400">No notification users selected.</p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1">
      {selected.map((s) => (
        <span
          key={s.value}
          className="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset"
          style={{
            backgroundColor: "#EEF2FF",
            color: "#4338CA",
            "--tw-ring-color": "#4338CA33",
          }}
        >
          {s.label}
        </span>
      ))}
    </div>
  );
}
